/**
 * `<AppShell/>` — the layout wrapper every authenticated route renders inside.
 *
 * Composition:
 *   - top nav (brand + team pill + Home · Roster · People · Setup + the
 *     panel toggle + user pill)
 *   - (#2048 — no breadcrumb row: the top nav already says where you are.
 *     Pages still register a trail with `useBreadcrumbs`; nothing shows it.)
 *   - <Outlet/> for the active route
 *   - footer with privacy / terms stub links
 *
 * Pages that don't want chrome (the user-picker on `/`) opt out by
 * rendering outside this layout — see `App.tsx`.
 */
import { ChevronLeft, ChevronRight, Menu, PanelRightOpen } from "lucide-react";
import {
  type RefObject,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { Link, Outlet, useLocation } from "react-router-dom";

import { useObservedTeam } from "../api/useObservedTeam";
import { useTeamFeature } from "../api/useTeamFeature";
import { useWorkspaces } from "../api/useWorkspaces";
import { useAuth } from "../lib/AuthContext";
import {
  getActiveWorkspaceId,
  getActiveWorkspaceRole,
  resolveInitialWorkspace,
  setActiveWorkspaceId,
  subscribeToWorkspaceChanges,
} from "../lib/activeWorkspace";
import { cn } from "../lib/cn";
import {
  persistentPanelContextRoot,
  setPersistentPanelOpen,
  usePersistentPanelOpen,
  usePersistentPanelVariant,
} from "../lib/persistentPanel";
import { usePersistentPanelBadgeCount } from "../lib/persistentPanelBadge";
import { ReadOnlyProvider, useReadOnly } from "../lib/teamAccess";
import type { TeamKind } from "../lib/teamApps";
import { useIsAtLeastLg } from "../lib/useMediaQuery";
import { usePageTransition } from "../lib/usePageTransition";
import { Logo } from "./Logo";
import { MemberPersistentPanel } from "./MemberPersistentPanel";
import { PersistentRightPanel } from "./PersistentRightPanel";
import { ThemePicker } from "./ThemePicker";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { HelpMenu } from "./help/HelpMenu";
import { FixAndResolveHost } from "./repair/FixAndResolveHost";
import { SeatKnockOnHost } from "./review/SeatKnockOnHost";
import { RecordAbsenceHost } from "./roster/RecordAbsenceHost";
import { AdminButton } from "./shell/AdminButton";
import { AppFooter } from "./shell/AppFooter";
import { DemoExitButton, DemoExitRow } from "./shell/DemoExitButton";
import { MobileDrawer } from "./shell/MobileDrawer";
import { ModuleSwitcher } from "./shell/ModuleSwitcher";
import { ObserverChip } from "./shell/ObserverChip";
import { UserPill } from "./shell/UserPill";
import {
  deriveNavLinks,
  isEdgeToEdgeRoute,
  pickActiveNavLink,
  shouldShowModuleSwitcher,
} from "./shell/navLinks";
import { SpotlightTour } from "./tour/SpotlightTour";
import { navTourAnchor } from "./tour/tourSteps";
import { isTourLeadRole } from "./tour/useTour";
import { SideDrawer } from "./ui/SideDrawer";
import { SlidingIndicator } from "./ui/SlidingIndicator";

// Re-exported for the existing import surfaces (App.tsx, the vitest
// suite, the #753 taskmap-shell regression test) — the decomposition
// moved these to `shell/*` but the public AppShell module contract
// stays byte-identical. `deriveNavLinks` + `isEdgeToEdgeRoute` are also
// imported above (the chrome below calls them) so they're re-surfaced
// here via the local binding.
export { deriveNavLinks, isEdgeToEdgeRoute };
export { type NavLinkSpec, type ModuleSwitcherEntry, MODULES } from "./shell/navLinks";
export { UserMenuActionList, type UserMenuActionListProps } from "./shell/UserPill";

/** localStorage kill-switch escape hatch (per implementation plan §4.7
 *  + the IA-2.2 brief). If a real lead's demo session is broken by the
 *  workspace switcher, set
 *  `localStorage["tarii.workspace_switcher_disabled"] = "1"` and the
 *  AppShell skips the mount. Reload required for the change to take
 *  effect (intentionally — flipping mid-session would split the
 *  user's mental model).
 *
 *  Issue 004 audit (2026-05-03): this key is OPT-IN ONLY. No SPA module
 *  may write to it programmatically — that would silently hide the
 *  switcher from the founder default (§4.3 renders at ≥1 workspace) and
 *  re-create the founder-UAT bug. A vitest case in
 *  `WorkspaceSwitcher.test.tsx` greps the SPA source tree to assert no
 *  `setItem(SWITCHER_KILL_SWITCH_KEY, ...)` call exists; if you ever
 *  need a programmatic disable, add a separate helper + update the
 *  test rather than reaching for this key directly. */
const SWITCHER_KILL_SWITCH_KEY = "tarii.workspace_switcher_disabled";

function workspaceSwitcherDisabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SWITCHER_KILL_SWITCH_KEY) === "1";
  } catch {
    return false;
  }
}

/** IA-2.5 — subscribe to `tarii.active_workspace_id` writes so the
 *  AppShell strip re-renders when the workspace switcher (or another
 *  tab) flips the active workspace. `useSyncExternalStore` is the
 *  React 18 primitive for non-React state with hot subscriptions —
 *  same pattern other Tarii surfaces use for theme + dev-shim id. */
function useActiveWorkspaceId(): string | null {
  return useSyncExternalStore(
    subscribeToWorkspaceChanges,
    getActiveWorkspaceId,
    () => null, // SSR snapshot — never reached in CSR but typed for completeness
  );
}

export function AppShell() {
  const { accountId } = useAuth();
  const switcherEnabled = !workspaceSwitcherDisabled();
  // #966 P3 (#970) — lead / co-lead / no-role mobile nav drawer open
  // state. Members never open this (they get the bottom-tab bar); the
  // hamburger + drawer only render on the `role !== "member"` mobile
  // branch below.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const isTeamHome = isEdgeToEdgeRoute(location.pathname);
  // Setup section runs wide (its own left sidebar + content), but —
  // unlike the edge-to-edge surfaces — it keeps the chrome's padding.
  // (Since 7 Oct 2026 every other page runs full-width too — the
  // `data-layout` names below are kept as the tests' and tour's anchors.)
  const isWideSetup = /^\/teams\/[^/]+\/setup(\/|$)/.test(location.pathname);
  // Route-restoration first-mount resolver (IA-2.3). Runs once when
  // the workspaces query first resolves; if `tarii.active_workspace_id`
  // is in localStorage but no longer maps to an active membership
  // (the user was removed from that team since last login, or signed
  // in as a different account), clear the stale value so the switcher
  // pill renders the most-recently-active workspace instead.
  useFirstMountWorkspaceResolver();
  // #2030 — and after first mount, the active workspace follows the team
  // in the address (a team just created, a link to another team).
  useActiveWorkspaceFollowsAddress();
  // #1920 — the panel handle sits just under the sticky header; publish
  // the header's height so it can.
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  useAppHeaderHeightVar(rootRef, headerRef);

  // IA-2.5 — derive the right-side strip from the active workspace's
  // role. The two reads (workspaces + active id) feed the pure
  // `getActiveWorkspaceRole` selector + `deriveNavLinks` shape. The
  // workspace switcher's role chip is the audited brand-pillar twin;
  // the strip's role-divergence is the §7.2 worker-friendly cut.
  const workspacesQuery = useWorkspaces();
  const storedWorkspaceId = useActiveWorkspaceId();
  // #2060 — an admin account on a team it is not a member of: the shell
  // follows the observed team (its nav, its chip) while the address names
  // it, and the page below is read-only. The stored active workspace stays
  // the caller's own.
  const observed = useObservedTeam();
  const activeWorkspaceId = observed ? observed.id : storedWorkspaceId;
  const workspaceRole = useMemo(
    () => getActiveWorkspaceRole(workspacesQuery.data, storedWorkspaceId),
    [workspacesQuery.data, storedWorkspaceId],
  );
  const role = observed ? ("observer" as const) : workspaceRole;
  // #753 — derive the active team's kind from the workspaces array (an
  // observed team carries its own). Defaults to 'scheduling'. A 'tasks'
  // team causes the nav strip to read from TEAM_APPS['tasks'].navItems
  // (Map · Members · Settings) per B7.2.
  const activeTeamKind = useMemo<TeamKind>(() => {
    if (observed) return observed.teamKind;
    if (!activeWorkspaceId || !workspacesQuery.data) return "scheduling";
    const ws = workspacesQuery.data.find((w) => w.team_id === activeWorkspaceId);
    return (ws?.team_kind as TeamKind | undefined) ?? "scheduling";
  }, [workspacesQuery.data, activeWorkspaceId, observed]);
  // #514 Layer 3 — per-team feature read drives the conditional `Swaps`
  // member nav entry. Only fetched when there's an active workspace
  // (the hook's `enabled` guard handles the undefined-team_id case);
  // `useTeamFeature` returns `undefined` while loading, which we coerce
  // to `false` so the link stays hidden until the read resolves
  // (avoids a brief flash-of-link on first paint).
  const swapRequestsEnabled =
    useTeamFeature(activeWorkspaceId ?? undefined, "swap_requests") === true;
  // #1282 — per-team feature read drives the conditional lead-strip
  // `Manpower` entry (same coerce-undefined-to-false posture as the
  // `swap_requests` read above; `manpower_planning` defaults ON in the
  // registry, so the link appears once the features query resolves).
  const manpowerPlanningEnabled =
    useTeamFeature(activeWorkspaceId ?? undefined, "manpower_planning") === true;
  const navLinks = useMemo(
    () =>
      deriveNavLinks(
        role,
        activeWorkspaceId,
        swapRequestsEnabled,
        activeTeamKind,
        manpowerPlanningEnabled,
      ),
    [role, activeWorkspaceId, swapRequestsEnabled, activeTeamKind, manpowerPlanningEnabled],
  );
  // #2172 — exactly one strip entry is current (the most specific claim),
  // so the gliding highlight has one place to be.
  const activeNavTo = pickActiveNavLink(navLinks, location.pathname);
  // #2048 — the module switcher only for an account with more than one
  // kind of team; otherwise the module previews sit in the account menu.
  const showModuleSwitcher = shouldShowModuleSwitcher(workspacesQuery.data, activeTeamKind);
  // #2048 — the panel toggle lives in the top bar. `<PanelAwareLayout/>`
  // (inside <main>) owns the panel and portals its toggle into this slot.
  const [panelToggleSlot, setPanelToggleSlot] = useState<HTMLElement | null>(null);

  return (
    <PanelToggleSlotContext.Provider value={panelToggleSlot}>
      <div ref={rootRef} className="flex min-h-screen flex-col bg-bg text-fg">
        {/* `pt-safe-top` clears the notch / status bar on a notched phone +
          the installed PWA (standalone) — `env(safe-area-inset-top)` is 0
          on desktop, so this is desktop-neutral (#966 polish). */}
        <header
          ref={headerRef}
          className="sticky top-0 z-40 border-b border-border bg-bg-elevated/85 pt-safe-top backdrop-blur"
        >
          <div className="flex w-full items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              {/* #966 P3 (#970) — mobile hamburger for the lead / co-lead /
                no-role nav drawer. Members get the bottom-tab bar instead,
                so this is gated on `role !== "member"`. Phone-only
                (`md:hidden`): the desktop inline nav strip takes over at
                `md`+, so the desktop top-bar is byte-unchanged. */}
              {role !== "member" ? (
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="flex min-h-tap min-w-tap items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent md:hidden"
                  data-testid="appshell-mobile-drawer-trigger"
                  aria-label="Open navigation"
                  aria-haspopup="dialog"
                  aria-expanded={drawerOpen}
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </button>
              ) : null}
              <Link to="/" className="shrink-0">
                <Logo />
              </Link>
              {/* #1907 / backlog #109 + #23 — the module + workspace
                switchers are the top bar's SECONDARY controls, and at
                360px they are what pushed the page to 755px wide: the
                hamburger, logo and user pill alone already fill the row,
                so nothing could shrink and the header forced a
                page-level sideways scroll on every route.
                Below `md` — exactly where the desktop nav strip is
                hidden and the hamburger appears — they collapse into the
                overflow that already holds them: `<MobileDrawer/>`
                renders the SAME `<ModuleSwitcher/>` + `<WorkspaceSwitcher/>`
                (see `showWorkspaceSwitcher` below), so each stays one tap
                away and nothing is dropped. Gated on the drawer actually
                being rendered (`role !== "member"`); a member has no
                hamburger, so their top bar keeps both inline. */}
              <div
                className={cn(
                  "min-w-0 items-center gap-3",
                  role !== "member" ? "hidden md:flex" : "flex",
                )}
              >
                {showModuleSwitcher ? <ModuleSwitcher /> : null}
                {switcherEnabled && accountId ? (
                  // #2048 — the spotlight tour's first stop.
                  <div data-tour="team-switcher" className="flex min-w-0">
                    <WorkspaceSwitcher />
                  </div>
                ) : null}
              </div>
              {/* #2060 — an observed team: the read-only chip beside the
                  switcher (desktop; on a phone it sits in the row below). */}
              {observed ? <ObserverChip className="hidden md:inline-flex" /> : null}
            </div>
            <nav
              className="relative isolate hidden items-center gap-1 md:flex"
              data-testid="appshell-nav-strip"
              data-role={role ?? "none"}
              data-team-kind={activeTeamKind}
            >
              {/* #2172 — the current-page fill glides between entries. */}
              <SlidingIndicator className="rounded-md bg-bg-subtle" />
              {navLinks.map((link) => {
                const current = link.to === activeNavTo;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    aria-current={current ? "page" : undefined}
                    data-tour={navTourAnchor(link.label)}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-sm",
                      // Focal-component discipline: the role-current
                      // most-frequented link reads with `font-medium`;
                      // supporting links recede to `font-normal`.
                      link.focal ? "font-medium" : "font-normal",
                      current ? "text-fg" : "text-fg-muted hover:bg-bg-subtle/60 hover:text-fg",
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
            <div className="flex shrink-0 items-center gap-1.5">
              {/* #2779 — in a `/demo` session: the way back to the website. */}
              <DemoExitButton />
              {/* #2902 — Tarii admin accounts: a visible Admin button. */}
              <AdminButton />
              {/* 7 Oct 2026 — the small ? Help button: the tour again (leads)
                and the in-app guides. */}
              <HelpMenu showTour={isTourLeadRole(workspaceRole) && !observed} />
              <ThemePicker />
              {/* #2048 — the panel toggle (portalled in by PanelAwareLayout
                on the routes that have a panel, from `lg` up). */}
              <div ref={setPanelToggleSlot} className="contents" data-testid="panel-toggle-slot" />
              {accountId ? <UserPill showModules={!showModuleSwitcher} /> : null}
            </div>
          </div>
          {/* #2779 — the demo exit below `xl` (no room in the bar). */}
          <DemoExitRow />
          {observed ? (
            <div className="mx-auto flex max-w-6xl px-4 pb-2 md:hidden">
              <ObserverChip />
            </div>
          ) : null}
          {/* Mobile nav (lead / co-lead / no-role): #966 P3 (#970) replaces
            the former horizontal-scroll strip with a hamburger → left
            slide-in drawer. The hamburger lives in the top-bar above
            (`appshell-mobile-drawer-trigger`); the drawer hosts the same
            role-derived `navLinks` plus the module + workspace switchers.
            Scheduling-team members never reach this shell at all (#1666
            — they live in the phone frame at `/me/*`), so the drawer is
            for leads / co-leads / no-role only. Phone-only: the desktop
            inline nav strip above takes over at `md`+. */}
          {role !== "member" && (
            <MobileDrawer
              open={drawerOpen}
              onOpenChange={setDrawerOpen}
              navLinks={navLinks}
              showWorkspaceSwitcher={switcherEnabled && Boolean(accountId)}
              showModuleSwitcher={showModuleSwitcher}
            />
          )}
        </header>
        <main
          // #1934 — a side panel opened by a deep link (no opener to return
          // to) hands focus here on close rather than dropping it on <body>
          // (`ui/SideDrawer` makes it focusable for that moment only); no
          // ring around the whole page when it does.
          className={cn(
            "flex-1 focus:outline-none",
            // Wave-6F (#479) — team home is edge-to-edge and owns its own
            // internal padding, so the `<main>` element passes no `px-/py-`
            // when full-width. Everywhere else runs the full width of the
            // window behind a gutter (7 Oct 2026: the founder on the old
            // centred `max-w-7xl` cap — "doesn't go to the edges of the
            // screen … really constrained"). A page that wants a narrow
            // reading measure (the Me pages, Activity) sets its own.
            isTeamHome
              ? "w-full"
              : isWideSetup
                ? "w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8"
                : "w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8",
          )}
          data-testid="appshell-main"
          data-layout={isTeamHome ? "edge-to-edge" : isWideSetup ? "wide" : "centered"}
        >
          <ReadOnlyProvider readOnly={Boolean(observed)}>
            <PanelAwareLayout />
            {/* #2060 — the write hosts never open on an observed team. */}
            {observed ? null : (
              <>
                {/* #1960 — Fix and re-solve, opened from the ladder or the inbox. */}
                <FixAndResolveHost />
                {/* #2029 — Record absence, opened from the roster toolbar. */}
                <RecordAbsenceHost />
                {/* #1964 — a ladder seat that moves someone: the knock-on, reviewed. */}
                <SeatKnockOnHost />
              </>
            )}
          </ReadOnlyProvider>
        </main>
        <AppFooter />
        {/* #2048 — the first-visit spotlight tour (and Help → "Show me around"). */}
        {accountId ? (
          <SpotlightTour accountId={accountId} workspaceRole={observed ? null : workspaceRole} />
        ) : null}
      </div>
    </PanelToggleSlotContext.Provider>
  );
}

/** #2048 — the top bar's slot for the panel toggle; `null` outside the
 *  shell (a unit test rendering `<PanelAwareLayout/>` alone), where the
 *  toggle falls back to the fixed edge handle. */
const PanelToggleSlotContext = createContext<HTMLElement | null>(null);

/**
 * IA-2.3 — first-mount route-restoration resolver.
 *
 * Runs once per page load. When the workspaces query resolves we apply
 * the implementation-plan §4.5 precedence to the URL + localStorage:
 *
 *   - URL is `/teams/:tid/...` → deep-link wins; persist that workspace
 *     id to localStorage so subsequent `/me/...` navigations show the
 *     right pill.
 *   - URL is `/cycles/:id/...` → the team id is not in the URL; we
 *     leave the resolver to `CycleRoutes` (it owns the cycle fetch)
 *     and only handle the localStorage stale-clear here.
 *   - URL is `/` or any other path → resolver returns the localStorage
 *     value if still in the membership list, else the most-recently-
 *     active workspace from the server ordering.
 *
 * Cases where the stored localStorage id is no longer in the workspaces
 * array (e.g. the user was removed from that team since last login)
 * trigger a `setActiveWorkspaceId(null)` to clear the stale value, so
 * the switcher's read returns the most-recently-active workspace
 * instead. Without the clear, the switcher would render the head row
 * but localStorage would still carry the dead id, and the next manual
 * write would overwrite it cleanly — the failure mode is cosmetic, not
 * load-bearing — but the brand pillar (trustable + worker-friendly)
 * argues for an honest cleared state.
 */
function useFirstMountWorkspaceResolver(): void {
  const workspacesQuery = useWorkspaces();
  const ranOnceRef = useRef(false);
  useEffect(() => {
    if (ranOnceRef.current) return;
    if (!workspacesQuery.data) return;
    ranOnceRef.current = true;

    // Derive the deep-link team id from the current URL. Matching
    // `/teams/:tid/...` only — `/cycles/:cid/...` doesn't carry the
    // team id, and CycleRoutes will write the active workspace once
    // the cycle row resolves. We read from `window.location` rather
    // than `useParams` so this hook can mount at the AppShell level
    // (above the per-route components).
    const path = typeof window === "undefined" ? "" : window.location.pathname;
    const match = path.match(/^\/teams\/([0-9a-f-]{36})(?:\/|$)/i);
    const deepLinkTeamId = match?.[1] ?? null;

    const resolved = resolveInitialWorkspace(workspacesQuery.data, deepLinkTeamId);

    // Stale-clear: if localStorage carries an id that's no longer in
    // the membership list, reset it. The resolver above already
    // returns `workspaces[0]` in that case, so the visible pill is
    // correct; we just sync localStorage to match.
    if (resolved !== null) {
      setActiveWorkspaceId(resolved);
    } else if (workspacesQuery.data.length === 0) {
      setActiveWorkspaceId(null);
    }
  }, [workspacesQuery.data]);
}

/**
 * #2030 — keep the active workspace on the team in the address.
 *
 * The first-mount resolver above runs once, with whatever team list is
 * cached at that moment. Right after `Create team` that list predates the
 * new team: onboarding sits outside the shell, so its invalidation only
 * marked the list stale, and the shell mounted on `/teams/<new>/setup/…`
 * with the old list — the resolver kept the stored previous team and the
 * switcher read its name until a reload. Following a link from one team's
 * page to another's had the same gap.
 *
 * So whenever the address names a `/teams/:tid` the caller belongs to (the
 * list is re-read as it refetches), that team becomes the active one.
 * Addresses without a team id (`/`, `/cycles/:id`, `/settings`) leave the
 * choice alone, and a team id not in the list is left to `<TeamNotFound/>`.
 */
function useActiveWorkspaceFollowsAddress(): void {
  const { pathname } = useLocation();
  const workspacesQuery = useWorkspaces();
  const addressTeamId = pathname.match(/^\/teams\/([0-9a-f-]{36})(?:\/|$)/i)?.[1] ?? null;
  useEffect(() => {
    if (!addressTeamId || !workspacesQuery.data) return;
    if (!workspacesQuery.data.some((w) => w.team_id === addressTeamId)) return;
    if (getActiveWorkspaceId() === addressTeamId) return;
    setActiveWorkspaceId(addressTeamId);
  }, [addressTeamId, workspacesQuery.data]);
}

/**
 * Publish the sticky header's rendered height as `--app-header-h` on the
 * shell root, so fixed chrome that sits just under the header — the panel
 * handle (#1920) — follows it whatever its height (the breadcrumb row, a
 * notch inset). A no-op where `ResizeObserver` is missing beyond one read.
 */
function useAppHeaderHeightVar(
  rootRef: RefObject<HTMLDivElement | null>,
  headerRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const root = rootRef.current;
    const header = headerRef.current;
    if (!root || !header) return;
    const publish = () => {
      const height = header.getBoundingClientRect().height;
      if (height > 0) root.style.setProperty(APP_HEADER_HEIGHT_VAR, `${height}px`);
    };
    publish();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, [rootRef, headerRef]);
}

/** The CSS variable `useAppHeaderHeightVar` writes. */
const APP_HEADER_HEIGHT_VAR = "--app-header-h";

/** #2048 — inside the shell the panel toggle sits in the top bar; the edge
 *  handle below is the fallback when `<PanelAwareLayout/>` renders without
 *  the shell (no top-bar slot). The geometry notes are its history. */
// The handle sits INSIDE the sticky header's breadcrumb row (the row is 3rem
// tall and its right half is always empty), not under the header: a tab
// placed below the header lands on the page's own top-right actions
// (`Configure team`, the roster toolbar) and, once the page scrolls, on
// content. 2.75rem up from the header's bottom edge centres a 2.5rem tab in
// that row. `z-40` is the header's own layer: the handle is later in the DOM
// than the header, so it paints over the header's blurred background, and
// the side panels (SideDrawer, z-40, portalled to the end of <body>) and the
// modal confirms' scrim (z-50) paint over the handle — at z-45 it drew over
// every open panel's header strip (r6 re-shoot, 2 Oct). While the next-steps
// drawer is open the handle rides the drawer's left edge, so it stays
// clickable as the close.
const PANEL_HANDLE_TOP = `calc(var(${APP_HEADER_HEIGHT_VAR}, 6.5rem) - 2.75rem)`;

/** Per-variant copy: the drawer title and the handle's accessible name. */
const PANEL_COPY = {
  lead: { title: "Next steps", openLabel: "Open next steps", closeLabel: "Close next steps" },
  member: { title: "Needs you", openLabel: "Open your panel", closeLabel: "Close your panel" },
} as const;

/**
 * Close the panel when the user moves to a DIFFERENT team or cycle
 * (`/teams/<id>` · `/cycles/<id>` · `/me` roots differ). Moving between
 * the pages of one cycle keeps it as it was. Paths outside the panel's
 * routes are skipped, so `/cycles/A` → `/settings` → `/cycles/B` still
 * closes it. A layout effect, so the next context never paints with the
 * previous one's drawer open.
 */
function useClosePanelOnContextChange(): void {
  const { pathname } = useLocation();
  const root = persistentPanelContextRoot(pathname);
  const lastRootRef = useRef<string | null>(root);
  useLayoutEffect(() => {
    if (root === null) return;
    const last = lastRootRef.current;
    lastRootRef.current = root;
    if (last !== null && last !== root) setPersistentPanelOpen(false);
  }, [root]);
}

/**
 * `<PanelAwareLayout/>` — IA-rework PR 1 (#194); overlay since #1920.
 *
 * The route `<Outlet/>` always renders in ONE full-width column — the
 * panel no longer takes a grid column, so it never shrinks the page (the
 * founder's 2 Oct brief: the side panel was jarring and squeezed the page).
 *
 * On lead-side `/teams/:tid*` + `/cycles/:cid*` and member-side `/me*`
 * paths, from `lg` (1024px) up, it adds:
 *
 *   - **the handle** — a tab fixed at the viewport's right edge just under
 *     the app header, with a count badge (`usePersistentPanelBadgeCount`)
 *     so a closed panel still says something is waiting;
 *   - **the drawer** — `ui/SideDrawer` (non-modal, no scrim, the page
 *     behind stays scrollable and clickable) holding the panel.
 *
 * **Closed by default** (`tarii.persistent_panel_open`, absent = closed;
 * see `lib/persistentPanel.ts`). The handle, `Esc` and a move to a
 * different team or cycle close it; the browser remembers an open panel.
 * Below `lg` neither mounts — phones keep the roster's own `…` menu.
 * The kill-switch (`tarii.persistent_panel_disabled`) removes both.
 */
export function PanelAwareLayout() {
  const variant = usePersistentPanelVariant();
  const atLeastLg = useIsAtLeastLg();
  const [open, setOpen] = usePersistentPanelOpen();
  useClosePanelOnContextChange();
  // #2060 — the Next steps panel lists a lead's to-dos; an observed team
  // has none for this account.
  const readOnly = useReadOnly();

  if (variant === null || readOnly) {
    return <RouteTransition />;
  }

  return (
    <div className="min-w-0" data-testid="panel-aware-layout" data-panel-variant={variant}>
      <RouteTransition />
      {atLeastLg ? (
        <PersistentPanelOverlay variant={variant} open={open} onOpenChange={setOpen} />
      ) : null}
    </div>
  );
}

/**
 * #2172 — the route `<Outlet/>` inside the element the page transition
 * animates (`lib/usePageTransition`). Only the route's own content sits in
 * it: the panel handle and drawer, the hosts' toasts and every other fixed
 * piece of chrome stay outside, so they never move while a page arrives.
 */
function RouteTransition() {
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  usePageTransition(ref, pathname);
  return (
    <div ref={ref} className="min-w-0" data-route-transition="">
      <Outlet />
    </div>
  );
}

function PersistentPanelOverlay({
  variant,
  open,
  onOpenChange,
}: {
  variant: "lead" | "member";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const badgeCount = usePersistentPanelBadgeCount(variant);
  const handleRef = useRef<HTMLButtonElement>(null);
  const copy = PANEL_COPY[variant];
  const panelId = `persistent-panel-${variant}`;
  const toggleSlot = useContext(PanelToggleSlotContext);

  // #2048 — inside the shell the toggle is a labelled button in the top
  // bar, next to the account pill (the founder: a lone "<" at the far
  // right of the breadcrumb row read as nothing). The drawer's own close
  // and `Esc` close it while open.
  const headerToggle = toggleSlot
    ? createPortal(
        <button
          ref={handleRef}
          type="button"
          onClick={() => onOpenChange(!open)}
          className={cn(
            "relative inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm",
            "text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            open && "bg-bg-subtle text-fg",
          )}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={open ? copy.closeLabel : copy.openLabel}
          data-state={open ? "open" : "closed"}
          data-placement="top-bar"
          data-testid="persistent-panel-toggle"
          data-tour="panel-toggle"
        >
          <PanelRightOpen className="h-4 w-4" aria-hidden="true" />
          <span>{copy.title}</span>
          {badgeCount > 0 ? (
            <span
              className="flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-fg-on-accent"
              data-testid="persistent-panel-badge"
            >
              {badgeCount}
            </span>
          ) : null}
        </button>,
        toggleSlot,
      )
    : null;

  return (
    <>
      {headerToggle}
      {/* Outside the shell (no top-bar slot): the edge handle — at the
          right edge while closed; while open it rides the drawer's left
          edge (the drawer is `md`, 360px) so it stays visible and closes
          it. */}
      {toggleSlot ? null : (
        <button
          ref={handleRef}
          type="button"
          onClick={() => onOpenChange(!open)}
          className={cn(
            "fixed z-40 flex h-10 w-10 items-center justify-center",
            "rounded-l-md border border-r-0 border-border bg-bg-elevated text-fg-muted shadow-sm",
            "transition-[right] duration-150 ease-out motion-reduce:transition-none",
            "hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent",
            open ? "right-[360px]" : "right-0",
          )}
          style={{ top: PANEL_HANDLE_TOP }}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={open ? copy.closeLabel : copy.openLabel}
          data-state={open ? "open" : "closed"}
          data-testid="persistent-panel-toggle"
        >
          {open ? (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          )}
          {badgeCount > 0 ? (
            <span
              className="pointer-events-none absolute -left-1.5 -top-1.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-fg-on-accent"
              data-testid="persistent-panel-badge"
            >
              {badgeCount}
            </span>
          ) : null}
        </button>
      )}
      <SideDrawer
        open={open}
        onOpenChange={onOpenChange}
        width="md"
        title={copy.title}
        testid="persistent-panel-drawer"
        returnFocusRef={handleRef}
      >
        <div id={panelId}>
          {variant === "lead" ? <PersistentRightPanel /> : <MemberPersistentPanel />}
        </div>
      </SideDrawer>
    </>
  );
}
